// /reports/time — admin tracker report: per employee, active time broken down by
// OS section (app_name) and by task, from ClickHouse native heartbeats. Reads
// GET /api/workspace/tracker/activity-breakdown. Standalone admin page (own brow).

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ArrowRight, Calendar, Clock, Loader2, Users, Building2, ListTodo } from 'lucide-react'
import api from '../services/api'
import AppLayout from '../components/layout/AppLayout'
import { PageBrow } from '../components/layout/PageBrow'
import { KpiCard, KpiRow } from '../components/patterns/KpiCard'
import { SectionCard } from '../components/patterns/SectionCard'

interface Entry { key: string; label: string; sec: number }
interface MemberBreakdown { memberId: string; name: string; totalSec: number; apps: Entry[]; tasks: Entry[] }
interface BreakdownResp { from: string; to: string; totalSec: number; members: MemberBreakdown[] }

function isoMinusDays(n: number) { return new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10) }
function todayIso() { return new Date().toISOString().slice(0, 10) }
function fmtH(sec: number) {
  if (!sec || sec <= 0) return '—'
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60)
  return h === 0 ? `${m}m` : `${h}h ${String(m).padStart(2, '0')}m`
}

function BreakdownList({ icon, title, entries, total }: { icon: ReactNode; title: string; entries: Entry[]; total: number }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {icon}{title}
      </div>
      {entries.length === 0 ? (
        <p className="text-xs text-muted-foreground">—</p>
      ) : (
        <div className="space-y-1">
          {entries.slice(0, 8).map((e) => {
            const pct = total > 0 ? Math.round((e.sec / total) * 100) : 0
            return (
              <div key={e.key} className="flex items-center gap-2 text-sm">
                <span className="truncate text-foreground" title={e.label}>{e.label}</span>
                <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">{fmtH(e.sec)}</span>
                <span className="w-8 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">{pct}%</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function ActivityBreakdown() {
  const [from, setFrom] = useState(isoMinusDays(6))
  const [to, setTo] = useState(todayIso())
  const [applied, setApplied] = useState({ from, to })
  const [data, setData] = useState<BreakdownResp | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    api.get('/workspace/tracker/activity-breakdown', { params: applied })
      .then((r) => { if (!cancelled) setData(r.data) })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error ?? 'Failed to load report') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [applied])

  const totalH = useMemo(() => (data ? fmtH(data.totalSec) : '—'), [data])

  return (
    <AppLayout>
    <div className="flex min-h-0 flex-1 flex-col">
      <PageBrow
        className="sticky top-0 z-20"
        left={<h1 className="text-[15px] font-semibold text-foreground">Time by app & task</h1>}
        right={
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-lg bg-card ring-1 ring-inset ring-border px-3 py-2">
              <Calendar size={14} className="text-muted-foreground" />
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-transparent text-sm text-foreground focus:outline-none" />
              <span className="text-xs text-muted-foreground">to</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-transparent text-sm text-foreground focus:outline-none" />
            </div>
            <button type="button" onClick={() => setApplied({ from, to })} className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90">
              Apply <ArrowRight size={12} />
            </button>
          </div>
        }
      />
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
        {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-300">{error}</div>}

        {data && (
          <KpiRow>
            <KpiCard label="Total tracked" value={totalH} icon={<Clock size={18} />} accent="text-emerald-500" />
            <KpiCard label="Employees" value={data.members.length} icon={<Users size={18} />} />
            <KpiCard label="Range" value={`${new Date(data.from).toLocaleDateString()} – ${new Date(data.to).toLocaleDateString()}`} />
          </KpiRow>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-16"><Loader2 size={20} className="animate-spin text-muted-foreground" /></div>
        ) : data && data.members.length === 0 ? (
          <div className="rounded-xl bg-card ring-1 ring-border px-4 py-16 text-center text-sm text-muted-foreground">
            No tracked time in this range yet. Time is captured automatically while people use the OS in the browser.
          </div>
        ) : (
          data?.members.map((m) => (
            <SectionCard key={m.memberId} icon={<Users size={15} />} title={m.name} subtitle={fmtH(m.totalSec)}>
              <div className="flex flex-col gap-6 sm:flex-row">
                <BreakdownList icon={<Building2 size={12} />} title="By OS section" entries={m.apps} total={m.totalSec} />
                <BreakdownList icon={<ListTodo size={12} />} title="By task" entries={m.tasks} total={m.totalSec} />
              </div>
            </SectionCard>
          ))
        )}
      </div>
    </div>
    </AppLayout>
  )
}
